import { PropsWithChildren, ReactNode, forwardRef } from 'react';

import { useInheritedVisualRefresh } from 'common/theme';
import { motion } from 'framer-motion';

import { BackdropLoader, Loader } from './components';

import type { SxProps, Theme } from '@mui/material';

const MotionDiv = ({ children }: PropsWithChildren) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ width: 'inherit', height: 'inherit' }}>
    {children}
  </motion.div>
);

type LoadableProps = {
  isLoading: boolean;
  transitionDelay?: boolean;
  sx?: SxProps<Theme>;
  animate?: boolean;
  children?: ReactNode;
  Skeleton?: ReactNode;
} & ({ backdrop: true; label?: string; size?: string | number } | { backdrop?: false; label: string; size?: 24 | 40 });

export const Loadable = forwardRef<HTMLElement, LoadableProps>((props, ref) => {
  const visualRefresh = useInheritedVisualRefresh();
  const { isLoading, children, transitionDelay = true, sx, animate = false, Skeleton } = props;

  if (props.backdrop) {
    return (
      <BackdropLoader ref={ref} sx={sx} isLoading={isLoading} isTransitionDelay={transitionDelay} size={props.size}>
        {children}
      </BackdropLoader>
    );
  }
  if (isLoading) {
    if (Skeleton) {
      return (
        <>
          {visualRefresh && (
            <span className="sr-only" role="status">
              {props.label}
            </span>
          )}
          {Skeleton}
        </>
      );
    }
    return <Loader ref={ref} label={props.label} sx={sx} isTransitionDelay={transitionDelay} size={props.size} />;
  }
  if (animate) {
    return <MotionDiv>{children}</MotionDiv>;
  }
  return <>{children}</>;
});
